// src/hooks/common/useToast.ts
import {useSelector, useDispatch} from "react-redux";
import {useCallback} from "react";
import {
  selectToasts,
  addToast,
  removeToast,
  clearToasts,
} from "../../store/slices/uiSlice";

type ToastType = "success" | "error" | "warning" | "info";

export const useToast = () => {
  const dispatch = useDispatch();
  const toasts = useSelector(selectToasts);

  const showToast = useCallback(
    (type: ToastType, title: string, message?: string, duration?: number) => {
      const toastPayload: any = {
        type,
        title,
      };

      if (message) {
        toastPayload.message = message;
      }

      if (duration) {
        toastPayload.duration = duration;
      }

      dispatch(addToast(toastPayload));
    },
    [dispatch]
  );

  const success = useCallback(
    (title: string, message?: string) => {
      showToast("success", title, message);
    },
    [showToast]
  );

  const error = useCallback(
    (title: string, message?: string) => {
      showToast("error", title, message);
    },
    [showToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => {
      showToast("warning", title, message);
    },
    [showToast]
  );

  const info = useCallback(
    (title: string, message?: string) => {
      showToast("info", title, message);
    },
    [showToast]
  );

  const remove = useCallback(
    (id: string) => {
      dispatch(removeToast(id));
    },
    [dispatch]
  );

  const clear = useCallback(() => {
    dispatch(clearToasts());
  }, [dispatch]);

  return {
    toasts,
    showToast,
    success,
    error,
    warning,
    info,
    remove,
    clear,
  };
};
